import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BobineLogo from '../components/BobineLogo';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live') + '/api';
const serif = "'Georgia', serif";

const AccountPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const token = localStorage.getItem('bobine_token');
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('bobine_user')); } catch { return null; } })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [story, setStory] = useState('');
  const [storySaving, setStorySaving] = useState(false);
  const [storySaved, setStorySaved] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/my-story`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setStory(d.story || '')).catch(() => {});
  }, [token]);

  const saveStory = async () => {
    setStorySaving(true);
    setStorySaved(false);
    try {
      const res = await fetch(`${API}/auth/update-story`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ story }),
      });
      if (res.ok) { setStorySaved(true); setTimeout(() => setStorySaved(false), 2500); }
    } catch { /* silent */ }
    setStorySaving(false);
  };

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/account/summary`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
        else navigate('/auth');
      } catch { navigate('/auth'); }
      setLoading(false);
    })();
  }, [token, navigate]);

  const openBilling = async () => {
    try {
      const res = await fetch(`${API}/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const d = await res.json();
      if (d.url) window.location.href = d.url;
    } catch {}
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/profile/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        setData(prev => ({ ...prev, user: { ...prev.user, has_profile_photo: true } }));
      }
    } catch {}
    setUploading(false);
  };

  // Fetch billing invoices
  useEffect(() => {
    if (!token) return;
    const fetchInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const res = await fetch(`${API}/billing/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          setInvoices(d.invoices || []);
        }
      } catch {}
      setInvoicesLoading(false);
    };
    fetchInvoices();
  }, [token]);

  if (loading || !data) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src="/bobine-face-splash.png" alt="" style={{ width: 80, animation: 'bobine-spin 4s linear infinite', opacity: 0.4 }} />
    </div>
  );

  const { user } = data;
  const planNames = { eveil: "L\u2019\u00c9veil", heritage: "L\u2019H\u00e9ritage", liberte_500: "Libert\u00e9 500", liberte_1000: "Libert\u00e9 1000", lignees: "L\u2019H\u00e9ritage" };
  const planLabel = planNames[user.plan] || user.plan;
  const isUnlimited = user.plan_limit === -1;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', color: '#fff' }}>
      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-6 flex items-center justify-between">
          {/* Bouton Retour — à gauche */}
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            style={{
              fontFamily: serif, fontSize: 12, fontWeight: 500,
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
              backgroundColor: 'transparent', color: '#D4AF37',
              border: '1px solid rgba(212,175,55,0.25)',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.3s',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.08)'; e.currentTarget.style.borderColor = '#D4AF37'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.25)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            Retour au Coffre-fort
          </button>

          <BobineLogo size={22} />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-lg">

        {/* Profile Card */}
        <div
          data-testid="profile-card"
          style={{
            textAlign: 'center', padding: '32px 24px',
            borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.04), rgba(212,175,55,0.01))',
            border: '1px solid rgba(212,175,55,0.15)',
            marginBottom: 32,
          }}
        >
          {/* Avatar */}
          <div
            data-testid="profile-avatar"
            onClick={() => fileRef.current?.click()}
            style={{
              width: 80, height: 80, borderRadius: '50%', margin: '0 auto 16px',
              border: '2px solid rgba(212,175,55,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(212,175,55,0.05)',
              cursor: 'pointer', overflow: 'hidden', position: 'relative',
              transition: 'all 0.3s',
            }}
          >
            {user.has_profile_photo ? (
              <img
                src={`${API}/profile/photo/${user.id}?t=${Date.now()}`}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            )}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: uploading ? 1 : 0,
              transition: 'opacity 0.3s',
            }}>
              {uploading ? (
                <div style={{ width: 24, height: 24, border: '2px solid rgba(212,175,55,0.3)', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="profile-photo-input"
              style={{ display: 'none' }}
              onChange={e => uploadPhoto(e.target.files[0])}
            />
          </div>
          <p style={{ fontFamily: serif, fontSize: 10, color: '#D4AF37', marginBottom: 12 }}>
            Cliquez pour ajouter une photo
          </p>

          <h2 style={{ fontFamily: serif, fontSize: 22, color: '#D4AF37', fontWeight: 400, margin: 0 }}>
            {user.name}
          </h2>
          <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginTop: 4 }}>
            {user.email}
          </p>
          {user.created_at && (
            <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', marginTop: 6 }}>
              {t('account_since') || 'Membre depuis'} {new Date(user.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Subscription */}
        <Section title={t('account_subscription') || 'Mon Abonnement'} testid="profile-subscription">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p style={{ fontFamily: serif, fontSize: 18, color: '#D4AF37', fontWeight: 400, margin: 0 }}>{planLabel}</p>
              <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', margin: '4px 0 0' }}>
                {isUnlimited ? 'Stockage Illimit\u00e9' : `${user.plan_limit} photos max`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(user.plan === 'heritage' || user.plan === 'lignees') && (
                <button data-testid="manage-subscription-btn" onClick={openBilling} style={{ ...btnOutline, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  {t('svc_manage_subscription') || 'Gérer mon abonnement'}
                </button>
              )}
              {user.plan !== 'heritage' && user.plan !== 'lignees' && (
                <button data-testid="upgrade-btn" onClick={() => navigate('/offres')} style={btnGold}>
                  {t('account_upgrade') || "Choisir l'Héritage"}
                </button>
              )}
            </div>
          </div>
        </Section>

        {/* Notre Histoire — texte de présentation libre */}
        <Section title="Notre Histoire" testid="story-section">
          <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', opacity: 0.7, marginBottom: 12, lineHeight: 1.5 }}>
            Quelques mots pour vous présenter, raconter la genèse de votre Bobine — ce texte pourra être affiché sur votre page publique pour toucher ceux qui découvrent votre sanctuaire.
          </p>
          <textarea
            data-testid="story-textarea"
            value={story}
            onChange={(e) => { setStory(e.target.value); setStorySaved(false); }}
            placeholder="Mon histoire commence ici…"
            maxLength={5000}
            rows={10}
            style={{
              width: '100%', boxSizing: 'border-box',
              fontFamily: serif, fontSize: 14, lineHeight: 1.6,
              padding: '14px 16px', borderRadius: 10,
              background: 'rgba(0,0,0,0.4)', color: '#FAF3E0',
              border: '1px solid rgba(212,175,55,0.25)', outline: 'none',
              resize: 'vertical',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.25)'; }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
            <span style={{ fontFamily: serif, fontSize: 11, color: '#D4AF37', opacity: 0.7 }}>
              {story.length} / 5000
            </span>
            <button
              data-testid="story-save-btn"
              onClick={saveStory}
              disabled={storySaving}
              style={{
                fontFamily: serif, fontSize: 13, fontWeight: 600, letterSpacing: 0.4,
                padding: '10px 26px', borderRadius: 999,
                cursor: storySaving ? 'default' : 'pointer',
                border: '1px solid rgba(184,134,11,0.85)',
                background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#1a1a1a',
                opacity: storySaving ? 0.6 : 1,
                boxShadow: '0 4px 14px rgba(212,175,55,0.3), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.2)',
              }}
            >
              {storySaving ? 'Enregistrement…' : storySaved ? '✓ Enregistré' : 'Enregistrer'}
            </button>
          </div>
        </Section>

        {/* Language */}
        <Section title={t('profile_language') || 'Langue'} testid="profile-language">
          <LanguageSelector />
        </Section>

        {/* Billing History */}
        <Section title="Historique de facturation" testid="billing-history">
          {invoicesLoading ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: 24, height: 24, margin: '0 auto', border: '2px solid rgba(212,175,55,0.2)', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : invoices.length === 0 ? (
            <p data-testid="no-invoices" style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
              Aucune facture pour le moment
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {invoices.map(inv => {
                const date = new Date(inv.date * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                const statusMap = {
                  paid: { label: 'Payee', color: '#27ae60', bg: 'rgba(39,174,96,0.1)' },
                  open: { label: 'En attente', color: '#D4AF37', bg: '#D4AF37' },
                  draft: { label: 'Brouillon', color: '#FAF3E0', bg: '#FAF3E0' },
                  void: { label: 'Annulee', color: '#FAF3E0', bg: '#FAF3E0' },
                  uncollectible: { label: 'Impayee', color: '#c0392b', bg: 'rgba(192,57,43,0.1)' },
                };
                const st = statusMap[inv.status] || { label: inv.status, color: '#FAF3E0', bg: '#FAF3E0' };

                return (
                  <div
                    key={inv.id}
                    data-testid={`invoice-${inv.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 10, gap: 10, flexWrap: 'wrap',
                      backgroundColor: 'rgba(212,175,55,0.02)',
                      border: '1px solid rgba(212,175,55,0.06)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', margin: 0 }}>
                        {inv.description || 'Facture Bobine'}
                      </p>
                      <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', margin: '3px 0 0' }}>
                        {date} {inv.number && <span> — {inv.number}</span>}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontFamily: serif, fontSize: 14, color: '#D4AF37', fontWeight: 600 }}>
                        {inv.amount.toFixed(2).replace('.', ',')} {inv.currency}
                      </span>
                      <span style={{
                        fontFamily: serif, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
                        padding: '3px 10px', borderRadius: 12, textTransform: 'uppercase',
                        color: st.color, backgroundColor: st.bg,
                      }}>
                        {st.label}
                      </span>
                      {inv.pdf_url && (
                        <a
                          href={inv.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`invoice-pdf-${inv.id}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, borderRadius: 8,
                            backgroundColor: 'rgba(212,175,55,0.06)',
                            border: '1px solid rgba(212,175,55,0.15)',
                            color: '#D4AF37', textDecoration: 'none',
                            transition: 'all 0.3s',
                          }}
                          title="Telecharger le PDF"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Actions */}
        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data?.user?.role === 'admin' && (
            <button
              data-testid="profile-admin-link"
              onClick={() => navigate('/admin')}
              style={{
                ...btnOutline, width: '100%', justifyContent: 'center',
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                color: '#000', borderColor: 'rgba(0,0,0,0.2)', fontWeight: 600,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3 6 6 1-4.5 4.5 1 6.5L12 17l-5.5 3 1-6.5L3 9l6-1z"/></svg>
              Administration
            </button>
          )}
          <button
            data-testid="profile-back-vault"
            onClick={() => navigate('/dashboard')}
            style={{
              ...btnOutline, width: '100%', justifyContent: 'center',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
            {t('dash_vault_title') || 'Mon Coffre-fort'}
          </button>
          <button
            data-testid="profile-logout"
            onClick={() => {
              localStorage.removeItem('bobine_token');
              localStorage.removeItem('bobine_user');
              navigate('/');
              window.location.reload();
            }}
            style={{
              fontFamily: serif, fontSize: 13, padding: '12px 20px', borderRadius: 8,
              cursor: 'pointer', backgroundColor: 'transparent',
              color: '#FAF3E0', border: '1px solid rgba(255,255,255,0.08)',
              width: '100%', transition: 'all 0.3s',
            }}
          >
            {t('dash_logout') || 'Quitter'}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </main>
    </div>
  );
};

const Section = ({ title, testid, children }) => (
  <div data-testid={testid} style={{
    padding: '20px', borderRadius: 14, marginBottom: 16,
    backgroundColor: 'rgba(212,175,55,0.02)',
    border: '1px solid rgba(212,175,55,0.10)',
  }}>
    <h3 style={{
      fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700,
      color: '#D4AF37', textTransform: 'uppercase',
      letterSpacing: 1.5, marginBottom: 14,
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const btnOutline = {
  fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 18px', borderRadius: 8,
  backgroundColor: 'transparent', color: '#D4AF37',
  border: '1px solid rgba(212,175,55,0.3)', cursor: 'pointer',
  transition: 'all 0.3s',
};

const btnGold = {
  fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600,
  padding: '10px 18px', borderRadius: 8,
  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer',
  transition: 'all 0.3s',
};

export default AccountPage;
